import React, { useState, useEffect } from 'react';
import { Check, Star, GearSix, Trophy, Heartbeat, ListChecks, SignOut, Lightning, ArrowClockwise } from '@phosphor-icons/react';
import { api } from '../data/api';

export default function More({ user, refreshUser, today, logout, showToast, customProgram, onRegeneratePlan }) {
  const [subTab, setSubTab] = useState(user.plan_type === 'custom' ? 'myplan' : 'hipfix');
  const [hipData, setHipData] = useState({});
  const [checklist, setChecklist] = useState({});
  const [rating, setRating] = useState({ stars: 0, mood: '' });
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    api.getHipFix(user.user_id).then(logs => {
      const todayLog = logs.find(l => l.date === today);
      if (todayLog) setHipData(todayLog.data || {});
    }).catch(() => {});
    api.getChecklist(user.user_id, today).then(logs => {
      if (logs.length > 0) setChecklist(logs[0].items || {});
    }).catch(() => {});
    api.getRatings(user.user_id).then(logs => {
      const todayLog = logs.find(l => l.date === today);
      if (todayLog) setRating({ stars: todayLog.stars || 0, mood: todayLog.mood || '' });
    }).catch(() => {});
    api.getAchievements(user.user_id).then(data => {
      setAchievements(data.achievements || []);
      setStats(data.stats || {});
    }).catch(() => {});
  }, [user.user_id, today]);

  const saveHipFix = async () => {
    await api.saveHipFix({ user_id: user.user_id, date: today, data: hipData });
    if (showToast) showToast('Hip work saved', 'success');
  };

  const toggleChecklist = async (key) => {
    const updated = { ...checklist, [key]: !checklist[key] };
    setChecklist(updated);
    await api.saveChecklist({ user_id: user.user_id, date: today, items: updated });
    if (showToast) showToast(updated[key] ? 'Checked' : 'Unchecked', 'success');
  };

  const saveRating = async () => {
    await api.saveRating({ user_id: user.user_id, date: today, ...rating });
    if (showToast) showToast('Rating saved', 'success');
  };

  const updateProfile = async (key, value) => {
    await api.updateUser(user.user_id, { [key]: value });
    refreshUser();
    if (showToast) showToast('Profile updated', 'success');
  };

  const isCustomUser = user.plan_type === 'custom' && customProgram;

  const tabs = [
    ...(isCustomUser ? [{ id: 'myplan', label: 'My Plan', Icon: Lightning }] : []),
    { id: 'hipfix', label: 'Hip Fix', Icon: Heartbeat },
    { id: 'checklist', label: 'Check', Icon: ListChecks },
    { id: 'achievements', label: 'Badges', Icon: Trophy },
    { id: 'rating', label: 'Rate', Icon: Star },
    { id: 'settings', label: 'Settings', Icon: GearSix },
  ];

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="more-page">
      {/* Page header */}
      <div className="glass rounded-xl p-3 sm:p-4">
        <div className="font-head text-lg sm:text-xl font-bold uppercase tracking-tight text-white">Tools & Settings</div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-0.5">Hip work, checklist, achievements & more</div>
      </div>

      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar pb-0.5">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex-shrink-0 flex items-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-bold uppercase border transition-all active:scale-95 ${
              subTab === t.id ? 'bg-brand text-white border-brand' : 'bg-elevated text-neutral-500 border-border'
            }`} data-testid={`more-tab-${t.id}`}>
            <t.Icon size={14} weight="bold" /> {t.label}
          </button>
        ))}
      </div>

      {/* My Plan (custom users only) */}
      {subTab === 'myplan' && isCustomUser && (
        <div className="space-y-3">
          <div className="bg-surface border border-accent/30 rounded-md p-4">
            <div className="font-head text-lg font-bold uppercase text-white">{customProgram.program_name || 'Custom Plan'}</div>
            <div className="text-xs text-neutral-400 mt-1">{customProgram.total_weeks || '—'} weeks / {customProgram.phases?.length || 0} phases</div>
            <div className="mt-3 space-y-2">
              {(customProgram.phases || []).map((ph, i) => (
                <div key={i} className="glass-elevated rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-white uppercase">{ph.name}</div>
                      <div className="text-[10px] text-neutral-500">{ph.weeks} weeks — {ph.focus}</div>
                    </div>
                    <div className="text-xs font-mono text-accent">{Object.keys(ph.workouts || {}).length} days/wk</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {Object.entries(ph.workouts || {}).map(([dk, dv]) => (
                      <span key={dk} className="text-[9px] bg-black/50 border border-border rounded px-1.5 py-0.5 text-neutral-400">
                        D{dk}: {dv.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {customProgram.nutrition && (
            <div className="glass rounded-xl p-4">
              <div className="font-head text-sm font-bold uppercase text-white mb-2">Nutrition Summary</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Calories', value: customProgram.nutrition.daily_calories, color: '#FF3B30' },
                  { label: 'Protein', value: `${customProgram.nutrition.protein_g}g`, color: '#007AFF' },
                  { label: 'Carbs', value: `${customProgram.nutrition.carbs_g}g`, color: '#FFD60A' },
                  { label: 'Fat', value: `${customProgram.nutrition.fat_g}g`, color: '#32D74B' },
                ].map(m => (
                  <div key={m.label} className="bg-black/50 border border-border rounded-md p-2 text-center">
                    <div className="font-mono text-sm font-bold" style={{ color: m.color }}>{m.value}</div>
                    <div className="text-[9px] text-neutral-500 uppercase font-bold">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={onRegeneratePlan}
            className="w-full flex items-center justify-center gap-2 bg-accent/10 border border-accent/30 text-accent font-head font-bold uppercase h-12 rounded-md active:scale-95 transition-all"
            data-testid="regenerate-plan-btn">
            <ArrowClockwise size={18} weight="bold" /> Regenerate Plan
          </button>
          <p className="text-[10px] text-neutral-600 text-center">This will replace your current plan with a new AI-generated one.</p>
        </div>
      )}

      {/* Hip Fix */}
      {subTab === 'hipfix' && (
        <div className="glass rounded-xl p-4 space-y-4">
          <div className="font-head text-lg font-bold uppercase text-white">Hip Fix Protocol</div>
          <p className="text-xs text-neutral-500 leading-relaxed">Daily corrective work for hip imbalance. QL stretch releases overactive right side, glute med activation stabilizes pelvis.</p>
          {[
            { key: 'ql', label: 'QL Stretch — Right Side', type: 'checkbox' },
            { key: 'bandWalk', label: 'Lateral Band Walk', type: 'checkbox' },
            { key: 'clamReps', label: 'Clamshell Reps (each side)', type: 'number' },
            { key: 'bridgeReps', label: 'Single-Leg Glute Bridge Reps', type: 'number' },
            { key: 'bssL', label: 'BSS Left Reps', type: 'number' },
            { key: 'bssR', label: 'BSS Right Reps', type: 'number' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between">
              <span className="text-sm text-neutral-300 font-medium">{item.label}</span>
              {item.type === 'checkbox' ? (
                <button onClick={() => setHipData(p => ({ ...p, [item.key]: !p[item.key] }))}
                  className={`w-12 h-12 flex items-center justify-center rounded-md border-2 transition-all active:scale-95 ${
                    hipData[item.key] ? 'bg-success border-success' : 'border-border'
                  }`} data-testid={`hip-${item.key}`}>
                  {hipData[item.key] && <Check size={20} weight="bold" className="text-black" />}
                </button>
              ) : (
                <input type="number" value={hipData[item.key] || ''}
                  onChange={(e) => setHipData(p => ({ ...p, [item.key]: parseInt(e.target.value) || 0 }))}
                  className="w-20 bg-black/50 border border-border text-white h-12 rounded-md px-3 font-mono text-center outline-none focus:border-brand"
                  data-testid={`hip-${item.key}`} />
              )}
            </div>
          ))}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 block mb-1">Hip Feel (1-10)</label>
            <div className="flex gap-1.5">
              {[...Array(10)].map((_, i) => (
                <button key={i} onClick={() => setHipData(p => ({ ...p, hipFeel: i + 1 }))}
                  className={`flex-1 h-10 rounded-md font-mono text-sm font-bold transition-all active:scale-95 ${
                    hipData.hipFeel === i + 1
                      ? i < 3 ? 'bg-success text-black' : i < 7 ? 'bg-warning text-black' : 'bg-brand text-white'
                      : 'bg-elevated text-neutral-500 border border-border'
                  }`} data-testid={`hip-feel-${i + 1}`}>{i + 1}</button>
              ))}
            </div>
          </div>
          <button onClick={saveHipFix} className="w-full bg-brand text-white font-head font-bold uppercase min-h-[48px] rounded-md active:scale-95 transition-all" data-testid="save-hipfix-btn">SAVE HIP WORK</button>
        </div>
      )}

      {/* Checklist */}
      {subTab === 'checklist' && (
        <div className="glass rounded-xl p-4 space-y-3">
          <div className="font-head text-lg font-bold uppercase text-white">Daily Checklist</div>
          {[
            { key: 'water', label: 'Drink 4L Water' },
            { key: 'sleep', label: '8 Hours Sleep' },
            { key: 'mobility', label: 'Mobility Work / Stretching' },
            { key: 'steps', label: '10K Steps' },
            { key: 'coldShower', label: 'Cold Shower / Exposure' },
            { key: 'noAlcohol', label: 'No Alcohol' },
            { key: 'noSmoking', label: 'No Smoking' },
          ].map(item => (
            <button key={item.key} onClick={() => toggleChecklist(item.key)}
              className={`w-full flex items-center gap-3 p-4 rounded-md border transition-all active:scale-[0.98] ${
                checklist[item.key] ? 'bg-success/10 border-success/30' : 'bg-elevated border-border'
              }`} data-testid={`checklist-${item.key}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-md border-2 ${
                checklist[item.key] ? 'bg-success border-success' : 'border-neutral-600'
              }`}>{checklist[item.key] && <Check size={20} weight="bold" className="text-black" />}</div>
              <span className={`font-medium ${checklist[item.key] ? 'text-success line-through' : 'text-white'}`}>{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Achievements */}
      {subTab === 'achievements' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Workouts', value: stats.total_workouts || 0, color: '#FF3B30' },
              { label: 'Total Reps', value: stats.total_reps || 0, color: '#007AFF' },
              { label: 'Streak', value: `${stats.streak || 0}d`, color: '#FFD60A' },
              { label: 'PRs Set', value: stats.total_prs || 0, color: '#32D74B' },
            ].map(s => (
              <div key={s.label} className="glass rounded-xl p-3 text-center">
                <div className="font-mono text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="font-head text-lg font-bold uppercase text-white">Achievements</div>
          <div className="grid grid-cols-2 gap-3">
            {achievements.map(ach => (
              <div key={ach.id}
                className={`bg-surface border rounded-md p-4 text-center transition-all ${
                  ach.unlocked ? 'border-warning bg-warning/5 scale-[1.02]' : 'border-border opacity-40'
                }`} data-testid={`achievement-${ach.id}`}>
                <div className="text-3xl mb-2">{ach.icon}</div>
                <div className="font-head text-sm font-bold uppercase text-white">{ach.name}</div>
                <div className="text-[10px] text-neutral-500 mt-1">{ach.desc}</div>
                {ach.unlocked && <div className="text-[10px] text-success font-bold uppercase mt-2">UNLOCKED</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rating */}
      {subTab === 'rating' && (
        <div className="glass rounded-xl p-4 space-y-4">
          <div className="font-head text-lg font-bold uppercase text-white">Workout Rating</div>
          <div className="flex justify-center gap-3">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => setRating(p => ({ ...p, stars: s }))}
                className={`text-4xl transition-all active:scale-110 ${rating.stars >= s ? 'text-warning' : 'text-neutral-700'}`}
                data-testid={`rating-star-${s}`}>★</button>
            ))}
          </div>
          <div className="font-head text-sm font-bold uppercase text-neutral-500 text-center">Mood</div>
          <div className="flex flex-wrap justify-center gap-2">
            {[{ id: 'energized', icon: '⚡' }, { id: 'tired', icon: '😴' }, { id: 'sore', icon: '🤕' }, { id: 'pumped', icon: '💪' }, { id: 'neutral', icon: '😐' }].map(m => (
              <button key={m.id} onClick={() => setRating(p => ({ ...p, mood: m.id }))}
                className={`px-4 py-2 rounded-full border text-sm font-bold uppercase transition-all active:scale-95 ${
                  rating.mood === m.id ? 'bg-brand text-white border-brand' : 'bg-elevated text-neutral-400 border-border'
                }`} data-testid={`mood-${m.id}`}>{m.icon} {m.id}</button>
            ))}
          </div>
          <button onClick={saveRating} className="w-full bg-brand text-white font-head font-bold uppercase min-h-[48px] rounded-md active:scale-95 transition-all" data-testid="save-rating-btn">SAVE RATING</button>
        </div>
      )}

      {/* Settings — personal only */}
      {subTab === 'settings' && (
        <div className="space-y-4">
          <div className="glass rounded-xl p-4 space-y-3">
            <div className="font-head text-lg font-bold uppercase text-white">My Profile</div>
            <div className="glass-elevated rounded-xl px-3 py-2 flex justify-between items-center">
              <span className="text-xs text-neutral-500 uppercase font-bold">User ID</span>
              <span className="font-mono text-sm text-neutral-400">{user.user_id}</span>
            </div>
            {[
              { key: 'name', label: 'Name', value: user.name, type: 'text' },
              { key: 'weight', label: 'Weight (kg)', value: user.weight, type: 'number' },
              { key: 'bf', label: 'Body Fat %', value: user.bf, type: 'number' },
              { key: 'height', label: 'Height (cm)', value: user.height, type: 'number' },
            ].map(field => (
              <div key={field.key}>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 block mb-1">{field.label}</label>
                <input type={field.type} defaultValue={field.value}
                  onBlur={(e) => updateProfile(field.key, field.type === 'number' ? parseFloat(e.target.value) : e.target.value)}
                  className="w-full bg-black/50 border border-border text-white h-12 rounded-md px-3 font-mono outline-none focus:border-brand"
                  data-testid={`settings-${field.key}`} />
              </div>
            ))}
          </div>

          {/* Logout */}
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 bg-elevated border border-red-500/30 text-red-400 font-bold uppercase min-h-[56px] rounded-md active:scale-95 transition-all"
            data-testid="logout-btn"
          >
            <SignOut size={20} weight="bold" /> Log Out / Switch Profile
          </button>
          <p className="text-[10px] text-neutral-600 text-center">Logs you out. Your data stays safe — log back in with your username & passcode anytime.</p>
        </div>
      )}
    </div>
  );
}
